// ------------------------------------------------------------ state & rendering
import * as SongBuzzEngine from '../core/engine.ts';
import { $, AWARD_ICON, CREAM, GENRE_COLOR, INK, LANG, SB, avatar, awardText, buzzerOf, connect, detectCountry, duel, duo, enableSpatialNav, esc, fitFlaps, flapIn, genreLabel, goldHtml, icon, isLight, jokerName, keepAwake, langSwitch, playerById, pointsWhy, ranked, reducedMotion, renderSettings, roundDesc, secsLeft, showField, store, swapNote, t, takeover, tMsg, track, trackHtml, twistText, typeTitle, upNextHtml, vinylSvg, watchVersion } from '../web/common.ts';
import { itemOf, legendOf, prizeOf } from '../core/wardrobe.ts';
import { A, H, STAGE, apiUrl, audioErr, gameSounds, hostCues, setAKey, soundOf, syncAudio } from '../web/stage.ts';
import { presenterShow } from '../web/presenter.ts';
import { S, conn, setConn, setS } from '../web/room.ts';
import type { DropWin, GameView, JokerKind, Phase, QuestionOption, ResultRow, Segment, StolenPoints, TimelineCard } from '../core/types.ts';
import type { KnownPlayer, Mood } from '../web/common.ts';
// The iOS app shows this page on an AirPlay TV and reaches into it from outside: it reads the room from `S` and turns the
// sound on with `A` and `unlock` (app/modules/external-screen, app/src/airplay.ts). Those names have to stay global, for
// the apps already installed too, or AirPlay stops working without a single error.
Object.defineProperty(window, 'S', { get: () => S });
window.A = A;
window.unlock = unlock;
// A follower TV: /tv?r=CODE puts a game running somewhere else on this screen, for a second house's living room. It shows
// and plays everything, a moment behind the game like a phone with the music on, and has no say: no keys, and nothing it
// does reaches the game (cloud.ts). The game ends its songs and writes its comments on the TV that runs it.
const roomCode = (v: unknown) => (typeof v === 'string' ? v : '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
let WATCH = SB.mode === 'cloud' ? roomCode(new URLSearchParams(location.search).get('r')) : '';
// A Cast receiver waits for the phone that cast it to say what it's for (castReceiver below): a room of its own, or
// following the room that phone is in. Anywhere else the page knows from its address.
const RECEIVER = /CrKey/.test(navigator.userAgent) || new URLSearchParams(location.search).has('cast');
// a Cast receiver and the app's AirPlay screen have nothing to type a code with
const CAN_TYPE = SB.mode === 'cloud' && !/CrKey|ExternalScreen/.test(navigator.userAgent) && !RECEIVER;
let lastPhase: Phase | null = null, lastSongKey: string | null | undefined = null, lastCount: number | null = null, gone = false;
STAGE.onAudioErr = (s: GameView) => { if (S === s) updateStage(s); track('error', { where: 'audio', song: s.song && s.song.id }); };
let songStartRanks = new Map<string, number>(); // pid -> chart position when the current song started, for Climbing / Dropping
// A ×2 stays between the player and their phone until the song has been going a few seconds, when it's too late for
// anyone to answer it with a joker. Only this page shows other people's jokers, so holding it back here is enough.
const X2_WAIT = 4000;
const x2Out = (s: GameView) => s.phase === 'reveal' || (s.phase === 'playing' && !!s.song && !!s.song.startedAt && conn!.now() - s.song.startedAt >= X2_WAIT);
const shownPlays = (s: GameView) => (s.jokerPlays || []).filter((x) => x.kind !== 'double' || x2Out(s));
// the presenter stands in when the state says so and mouths whatever the host says
const PS = presenterShow($('#presenter'), () => A.voiceLevels());
STAGE.onLine = (q) => PS.say(q);
STAGE.onLineEnd = () => PS.done();
let started = false, noRoom = '';
function start() {
  if (started) return;
  started = true;
  if (WATCH) { STAGE.leads = false; STAGE.remote = true; track('follower_tv', { cast: RECEIVER }); }
  setConn(connect(WATCH ? 'watch' : 'tv', handlers, { room: WATCH }));
}
const handlers: Parameters<typeof connect>[1] = {
  onState(s) { setS(s); render(); syncAudio(s); },
  onFx(m) {
    // the AI's comment on the game, written on the TV that runs it, which already says it itself
    if (m.fx === 'comment') { if (WATCH && H.enabled()) H.say(m.q || { c: m.c }, 14000, () => !['playing', 'buzzed'].includes(S!.phase)); return; }
    if (m.fx === 'joker' && m.kind === 'double') return; // it comes out a few seconds into the song (x2News)
    const sound = soundOf(m); if (sound) A.sfx(sound, S ? Math.max(0, S.players.findIndex((p) => p.pid === m.pid)) : 0, buzzerOf(S, m.pid));
    if (m.fx === 'joker' && S) { jokerBanner(S, m); if (m.kind === 'freeze') PS.react('shock', 1800); }
  },
  onConn(ok) { document.body.classList.toggle('offline', !ok); },
  onNoRoom(code) { noRoom = code; noRoomHere(code); },
  // a code still on the books with nothing running it: nobody will ever send this screen a thing, so it says so
  onGone(on) {
    gone = on;
    document.body.classList.toggle('roomgone', on);
    if (on) { A.stop(); A.bed(false); A.hush(); setAKey(null); $('#paused').hidden = true; $('#flash').classList.remove('flashon'); $('#flash').dataset.sig = ''; noRoomHere(WATCH!, `${t('Nothing is running in room {code} right now.', { code: esc(WATCH!) })} ${t('If the game comes back, this page will rejoin it by itself.')}`); }
    else if (S) { lastPhase = null; render(); syncAudio(S); }
  },
  onError(msg) { $('#stage').innerHTML = `<div class="nowatch"><div class="error">${icon('cross')} ${esc(msg)}</div><button class="btn go" id="retryBtn">${t('Try again')}</button></div>`; $('#retryBtn').onclick = () => location.reload(); $('#retryBtn').focus(); },
};
if (!RECEIVER || WATCH) start();
enableSpatialNav();
keepAwake();
watchVersion(() => !S || (['lobby', 'gameover'].includes(S.phase) && !document.body.classList.contains('customizing')));
addEventListener('resize', () => { fitFlaps($('#stage')); if ($('#flash').classList.contains('flashon')) fitFlaps($('#flash')); });
document.title = t('SongBuzz TV');
if (/ExternalScreen/.test(navigator.userAgent)) document.body.classList.add('external'); // the app's AirPlay screen

// Sound: try straight away (Cast, TV apps and some browsers allow it), otherwise ask for one press of OK.
const soundBtn = $('#soundKey');
// Browsers keep sound locked until the first click or key press, and the unlock never resolves
// before that, so show the cue straight away instead of waiting on it.
function unlock() {
  const ready = () => { updateSound(); if (A.running() && S) syncAudio(S); };
  A.init().then(ready, ready);
  setTimeout(ready, 400);
}
// Coming from the home page's "This screen is the TV" already counts as the press browsers want, so the sound
// usually starts by itself. If not, the "Turn sound on" key in the header is the one way to turn it on; before
// a song it turns yellow and takes the focus, because then it really needs that one press.
const SONG_PHASES = ['countdown', 'playing', 'buzzed', 'reveal'];
let soundPressed = false, soundTracked = false;
function updateSound() {
  // whether the sound started by itself or needed the press, and when
  if (A.running() && !soundTracked) { soundTracked = true; track('sound_on', { pressed: soundPressed, phase: (S && S.phase) || 'lobby' }); }
  const need = !A.running() && !!S && SONG_PHASES.includes(S.phase);
  soundBtn.hidden = A.running();
  soundBtn.classList.toggle('urgent', need);
  if (need && !document.activeElement!.closest('main,#ctrls')) soundBtn.focus();
}
soundBtn.innerHTML = `${icon('sound')} ${t('Turn sound on')}`;
$('#connMsg').textContent = t('Reconnecting…');
soundBtn.onclick = () => { soundPressed = true; unlock(); };
// Pause: the header key, the big Resume on the paused card, and a remote's play/pause key (or P on a keyboard).
function togglePause() { if (S && S.phase !== 'lobby') conn!.send({ t: S.paused ? 'resume' : 'pause' }); }
$('#resumeBtn').onclick = () => conn!.send({ t: 'resume' });
$('#resumeBtn').textContent = t('Resume');
document.addEventListener('keydown', (e) => {
  if (['MediaPlayPause', 'MediaPlay', 'MediaPause'].includes(e.key) || (e.key === 'p' || e.key === 'P') && !(e.target as Element).closest('input, textarea')) {
    if (e.key === 'MediaPlay' && !(S && S.paused)) return;
    if (e.key === 'MediaPause' && S && S.paused) return;
    e.preventDefault(); togglePause();
  }
});
let stillT = 0;
function stirred() { document.body.classList.remove('still'); clearTimeout(stillT); stillT = setTimeout(() => document.body.classList.add('still'), 3000); }
document.addEventListener('mousemove', stirred);
document.addEventListener('keydown', stirred, true);
// the remote's keys, in the menus: the sofa hears that it did something
document.addEventListener('keydown', (e) => { if (S && ['lobby', 'gameover'].includes(S.phase)) { if (e.key.startsWith('Arrow')) A.sfx('nav'); else if (e.key === 'Enter') A.sfx('select'); } });
stirred();
unlock();

function focusFirst() {
  if (!S || WATCH || ['countdown', 'playing', 'buzzed'].includes(S.phase)) return; // never pre-focus Skip mid-song, and a follower has nothing to press
  if (soundBtn.classList.contains('urgent')) return soundBtn.focus();
  const b = $('#startBtn') || $('#againBtn') || $('#nextBtn') || $('#stage button');
  if (b && !document.activeElement!.closest('main,#ctrls')) b.focus();
}

const joinHost = (s: GameView) => { try { const u = new URL(s.joinUrl); return u.host + (s.room ? '' : u.pathname); } catch (e) { return s.joinUrl; } };
const qr = (s: GameView) => apiUrl('/qr.svg?u=' + encodeURIComponent(s.joinUrl));
const pad2 = (n: number) => String(n).padStart(2, '0');

// The label sits in the cassette's trapezoid; its size follows the words so any language fits inside it. The reels are
// drawings of their own: a whole drawing turns by itself, a part of one has the page redraw it every frame.
const tapeSvg = () => { const label = t('now playing'), size = Math.min(18, 124 / (label.length * .68)).toFixed(1); return `<div class="tape"><svg viewBox="0 0 260 150" aria-hidden="true"><rect x="4" y="4" width="252" height="142" rx="16" fill="${CREAM}" stroke="${INK}" stroke-width="5"/>
  <rect x="26" y="24" width="208" height="62" rx="10" fill="${INK}"/>
  <path d="M48 146 L64 104 H196 L212 146" fill="none" stroke="${INK}" stroke-width="5"/><text x="130" y="132" text-anchor="middle" font-family="Shrikhand, Georgia, serif" font-size="${size}" fill="${INK}">${esc(label)}</text></svg>
  ${[25.4, 59.2].map((x) => `<svg class="reel" style="left:${x}%" viewBox="0 0 40 40" aria-hidden="true"><circle cx="20" cy="20" r="20" fill="${CREAM}"/>${[0, 60, 120].map((a) => `<rect x="18" y="2" width="4" height="36" fill="${INK}" transform="rotate(${a} 20 20)"/>`).join('')}</svg>`).join('')}</div>`; };

function render() {
  if (gone) return;
  const s = S!;
  document.body.dataset.phase = s.phase;
  document.body.classList.toggle('jk', !!s.settings.jokers);
  const pz = $('#paused'), wasPaused = !pz.hidden;
  pz.hidden = !s.paused;
  // the show paused itself: a duel's other phone dropped out (engine waitForPhone)
  $('#pausedTitle').textContent = s.waitFor ? t('Waiting for {name}', { name: playerById(s, s.waitFor).name }) : t('Paused');
  $('#pausedTitle').classList.toggle('wait', !!s.waitFor);
  $('#pausedHint').textContent = WATCH ? t('The show is paused. Back in a moment.') : s.waitFor ? t("Their phone dropped out. The show carries on when it's back, or press Resume to play on.") : t('Press play on the remote, or Resume, to carry on.');
  $('#resumeBtn').hidden = !!WATCH;
  if (s.paused && !wasPaused) { A.hush(); if (!WATCH) $('#resumeBtn').focus(); }
  const field = showField(s);
  document.body.style.setProperty('--field', field);
  document.body.classList.toggle('light', isLight(field));
  roomPlate();
  if (s.phase === 'countdown' && lastPhase !== 'countdown') songStartRanks = new Map(ranked(s.players).map((p) => [p.pid, p.pos]));
  if (s.phase === 'lobby') songStartRanks = new Map();
  if (s.phase !== lastPhase || (s.song && s.song.key !== lastSongKey)) {
    buildStage(s);
    requestAnimationFrame(() => fitFlaps($('#stage')));
    document.fonts && document.fonts.ready.then(() => fitFlaps($('#stage')));
  }
  updateStage(s);
  renderChart(s);
  jokerNewsTv(s);
  renderCtrls(s);
  updateSound();
  fitStage();
  // on the final board the presenter stands by the winner: where that is depends on how much else is on the board
  const hero = s.phase === 'gameover' && document.body.dataset.step === 'winner' && $('.final .hero');
  if (hero) { const r = hero.getBoundingClientRect(), rem = parseFloat(getComputedStyle(document.documentElement).fontSize); document.body.style.setProperty('--final-x', (r.right + 3 * rem) + 'px'); document.body.style.setProperty('--final-y', -(innerHeight - r.bottom) - rem + 'px'); document.body.style.setProperty('--final-k', Math.min(.37, (r.bottom - rem - $('.head').getBoundingClientRect().bottom) / (32 * rem)).toFixed(3)); }
  PS.update(s);
  hostCues(s);
  gameSounds(s);
  lastPhase = s.phase; lastSongKey = s.song && s.song.key;
}


// With many players the cast takes two rows and the stage gets shorter: what's on it shrinks to fit above the cast
// (never grows). The lobby, the intro, the standings and the final board size themselves.
function fitStage() {
  const st = $('#stage'), el = st.firstElementChild as HTMLElement | null;
  if (!el || ['lobby', 'intro', 'stand', 'final', 'loading'].some((c) => el.classList.contains(c))) return;
  // its natural height: laid out at height auto, so nothing (the cassette's row) gets squeezed while measuring
  el.style.zoom = ''; el.style.height = 'auto';
  // room to spare: the tilted sleeve reaches past its box, and the cast's points pops ("+3") rise about 3rem above
  // the faces, over the bottom of the stage (when the stage keeps a news band at its foot, that band holds them)
  const cs = getComputedStyle(st), band = parseFloat(cs.paddingBottom) || 0;
  const spare = parseFloat(getComputedStyle(document.documentElement).fontSize) * (band ? 1 : 3.4);
  const need = el.getBoundingClientRect().height + spare, have = st.clientHeight - band - (parseFloat(cs.paddingTop) || 0);
  el.style.height = '';
  if (need <= have) return;
  const k = Math.max(0.55, have / need);
  el.style.zoom = k.toFixed(3); el.style.height = (have / k).toFixed(1) + 'px';
}
addEventListener('resize', () => requestAnimationFrame(fitStage));
const segLine = (g: Segment) => t('Round {n} of {m}', { n: g.number, m: g.of });
// The header's corner: the language in the lobby, and how to join (a little ticket with the QR) once the lobby's big
// QR card is gone: in the game, and while Customize has the screen.
function roomPlate() {
  const s = S; if (!s) return;
  const rm = $('#room'), custom = document.body.classList.contains('customizing'), lobby = s.phase === 'lobby';
  const rsig = (s.room || 'lan') + lobby + custom + LANG;
  if (rm.dataset.sig === rsig) return;
  rm.dataset.sig = rsig;
  const plate = `<div class="joinplate"><img src="${esc(qr(s))}" alt="${esc(t('QR code to join'))}"><div>${s.room
    ? `<small>${t('Join at {host}', { host: esc(joinHost(s)) })}</small><b>${esc(s.room)}</b>`
    : `<small>${t('Scan to play')}</small><b class="host">${esc(joinHost(s))}</b>`}</div></div>`;
  rm.innerHTML = lobby ? langSwitch() + (custom ? plate : '') : plate;
}
// Customize swaps the programme for every option; the TV gives it the whole screen while it is open.
// The options are set as large as the screen allows, so they read from the sofa.
let fitting = false;
function syncCustomizing() {
  const on = !!document.querySelector('#settings .custom'), setup = $('.setup');
  document.body.classList.toggle('customizing', on);
  roomPlate();
  PS.refresh();
  if (S && S.phase === 'lobby') lobbyTitle(S);
  if (!setup || fitting) return;
  fitting = true;
  if (!on) setup.style.fontSize = '';
  else { let size = 1.45; setup.style.fontSize = size + 'rem'; while (setup.scrollHeight > setup.clientHeight + 1 && size > .75) { size -= .05; setup.style.fontSize = size.toFixed(2) + 'rem'; } }
  // the observer would see our own style change; let it pass
  setTimeout(() => { fitting = false; });
}
addEventListener('resize', syncCustomizing);
new MutationObserver(syncCustomizing).observe($('#stage'), { childList: true, subtree: true });
if (document.fonts) document.fonts.ready.then(syncCustomizing);
// With a remote, the focused key always stays on screen.
$('#stage').addEventListener('focusin', (e) => { if ((e.target as Element).closest('.setup')) (e.target as Element).scrollIntoView({ block: 'nearest' }); });
// One goal per screen. Some moments have more to say than one screen should, so they run as steps inside the game's
// own timing: the reveal is the answer, then the points; the standings are the table, then last place's pick; the end
// of the game is the winner, then the awards, then the final table. The step is on <body> for the CSS and the presenter.
type Step = [name: string, ms?: number];
let stepT = 0, chimes: number[] = [];
function setStep(step: string) {
  document.body.dataset.step = step || ''; PS.refresh(); fitFlaps($('#stage')); if (step === 'board') { fitNames(document); focusFirst(); }
  if (step === 'awards' && S && S.awards) chimes = S.awards.map((_, i) => window.setTimeout(() => A.sfx('award', i), 300 + i * 1200));
}
function steps(list: Step[]) {
  clearTimeout(stepT); chimes.forEach(clearTimeout); chimes = []; // (Play again during the awards: no chimes over the next game)
  const go = (i: number) => { setStep(list[i][0]); if (i + 1 < list.length) stepT = setTimeout(() => go(i + 1), list[i][1]); };
  go(0);
}
function buildStage(s: GameView) {
  const st = $('#stage');
  clearTimeout(stepT); chimes.forEach(clearTimeout); chimes = []; document.body.dataset.step = '';
  if (s.phase !== 'buzzed') $('#flash').classList.remove('flashon');
  if (s.phase === 'lobby') {
    st.innerHTML = `
      <div class="lobby">
        <div class="plate"><img src="${esc(qr(s))}" alt="${esc(t('QR code to join'))}"><h2>${t('Scan to play')}</h2>${s.room ? `<div class="code">${esc(s.room)}</div><small>${t('or open {host}', { host: esc(joinHost(s)).replace(/\./g, '.<wbr>') })}</small>` : `<small>${t('or open {host}', { host: esc(s.joinUrl).replace(/([/:.])/g, '$1<wbr>') })}</small>`}</div>
        <div class="setup">
          <h1 id="lobbyTitle">${t("Who's playing?")}</h1>
          <div class="start-row"><span id="startHint"></span></div>
          <div id="settings"></div>
          <div id="nightBox"></div>
          <div id="lobbyErr"></div>
          <div id="hostJoin"></div>
          ${WATCH || !CAN_TYPE ? '' : `<div class="watchjoin" id="watchJoin"><button class="linkish" id="watchBtn">${t('Join another room')}</button></div>`}
        </div>
      </div>`;
      const wb = $('#watchBtn'); if (wb) wb.onclick = () => { $('#watchJoin').innerHTML = watchForm(); bindWatchForm(); };
  } else if (s.phase === 'loading') {
    st.innerHTML = `<div class="loading">${vinylSvg('#ffc531')}<h1>${t('Picking the songs…')}</h1></div>`;
  } else if (s.phase === 'intro') {
    const g = s.segment!;
    st.innerHTML = `<div class="intro">${vinylSvg(isLight(showField(s)) ? '#ff6a3d' : '#ffc531', 'disc')}
      <div class="rn">${segLine(g)}</div><h1>${esc(typeTitle(g.type))}</h1>
      <p class="how">${esc(roundDesc(s, g.type))}</p>
      ${g.mult > 1 ? `<div class="stickers"><span class="x2">${g.number === g.of ? t('Final round · double points') : g.twistBy ? esc(t('Double points, picked by {name}', { name: playerById(s, g.twistBy).name })) : t('Double points')}</span></div>` : ''}</div>`;
  } else if (s.phase === 'drop') {
    // the Joker Drop's sting: no countdown, straight into the song
    st.innerHTML = `<div class="sting"><div class="bolt" aria-hidden="true">${duo('lightning')}</div><h1>${t('Joker Drop!')}</h1><p>${duel(s) ? t("{n} seconds of a song. The fastest right answer wins a joker, and whoever's behind wins one too if they get it.", { n: SongBuzzEngine.DROP_SECS }) : t('{n} seconds of a song. The fastest right answer wins a joker, and so does the fastest from the bottom half of the table.', { n: SongBuzzEngine.DROP_SECS })}</p></div>`;
  } else if (s.phase === 'countdown') {
    lastCount = null;
    const hummer = s.hum && s.hum.pid && playerById(s, s.hum.pid);
    st.innerHTML = `<div class="count ${s.settings.jokers ? 'jk' : ''}"><div class="big"><span id="cnum">${secsLeft(conn!, s.phaseEndsAt!) || 3}</span><p>${t('Song {n} of {m}', { n: s.segment!.index, m: s.segment!.count })}</p>${swapNote(s)}${hummer ? `<div class="hums">${avatar(hummer, 'grin')}<span>${esc(t('{name} hums this one', { name: hummer.name }))}</span></div>` : ''}</div><div class="plays" id="plays"></div></div>`;
  } else if (s.phase === 'playing' || s.phase === 'buzzed') {
    if (!$('#playHead') || lastSongKey !== s.song.key || !['playing', 'buzzed'].includes(lastPhase!)) {
      st.innerHTML = `<div class="play"><div id="playHead"></div>${s.song.type === 'hum' ? '' : tapeSvg()}<div id="playBody"></div></div><div class="clockc" id="clock"></div><div id="toastBox"></div>`;
      buildPlayHead(s);
    }
  } else if (s.phase === 'reveal') {
    const r = s.reveal, tlr = s.song.type === 'timeline' && s.timeline;
    const last = s.round >= s.total;
    const sleeve = `<div class="sleeve">${vinylSvg('#ffc531')}<img src="${esc(r.cover || '')}" alt=""></div>`;
    const hummedBy = r.hummer ? playerById(s, r.hummer) : null, pickedBy = r.pickedBy ? playerById(s, r.pickedBy) : null;
    const words = `<div class="it">${t('It was')}</div><div class="song" data-fit="${tlr ? 60 : 84}" data-lines="2">${esc(r.title)}</div><div class="art">${esc(r.artist)}</div>${r.year && ['year', 'timeline'].includes(s.song.type) ? `<div class="yr">${r.year}</div>` : ''}`
      + (r.lyric ? `<div class="lyr-ans"><span>“${esc(r.lyric.prev)}”</span><b>${esc(r.lyric.line)}</b></div>` : '')
      + (hummedBy ? `<div class="hummed">${avatar(hummedBy, 'grin')}<span>${esc(t('Hummed by {name}', { name: hummedBy.name }))}</span></div>` : '')
      + (pickedBy ? `<div class="hummed">${avatar(pickedBy, 'grin')}<span>${esc(t('Picked by {name}', { name: pickedBy.name }))}</span></div>` : '');
    const scored = r.results.some((x) => x.pts || x.stolenBy), nobody = !r.drop && !scored ? `<p class="nob">${t('Nobody got this one')}</p>` : '';
    st.innerHTML = r.medley
      ? `<div class="mrv"><h1 class="q">${t('Golden Medley')}</h1><div class="mgrid">${r.medley.map((x, i) => `<div class="m1" style="--i:${i}"><img src="${esc(x.cover || '')}" alt=""><b>${esc(x.title)}</b><i>${esc(x.artist)}</i></div>`).join('')}</div>${nobody}</div><div class="nextin" id="nextIn"></div>`
      : tlr
      ? `<div class="tlw"><div class="rv tlr">${sleeve}<div>${words}${nobody}</div></div>${timelineHtml(s.timeline!, s.placedAt)}</div><div class="nextin" id="nextIn"></div>`
      : `<div class="rv">${sleeve}<div>${words}${r.drop ? dropWinners(s, r.drop) : nobody}</div></div><div class="nextin" id="nextIn"></div>`;
    $('#nextIn').dataset.last = last ? '1' : '';
    // then the points, for the rest of the reveal
    if (scored && !r.drop) { document.body.dataset.step = 'answer'; PS.refresh(); stepT = setTimeout(pointsStep, Math.min(4500, Math.max(1500, (s.phaseEndsAt! - conn!.now()) * .4))); }
  } else if (s.phase === 'standings' && s.standings) {
    const sd = s.standings, many = sd.rows.length > 8;
    const rows = sd.rows.map((x, i) => {
      const p = playerById(s, x.pid), d = x.was - x.pos;
      const mv = d > 0 ? `<span class="mv up">▲${d}</span>` : d < 0 ? `<span class="mv down">▼${-d}</span>` : '';
      return `<div class="s1 ${x.pos === 1 ? 'top' : ''}" style="--i:${i}"><span class="n">${x.pos}</span>${avatar(p, x.pos === 1 ? 'grin' : i === sd.rows.length - 1 && i > 1 && x.pos > 1 ? 'sad' : 'smile')}<span class="nm">${esc(p.name)}</span>${mv}<span class="g ${x.gain < 0 ? 'neg' : x.gain ? '' : 'zero'}">${x.gain > 0 ? '+' : ''}${x.gain}</span><b>${x.score}</b></div>`;
    }).join('');
    const shead = `<div class="shead"><span>${t('This round')}</span><span>${t('Total')}</span></div>`;
    steps([['table', Math.min(5000, Math.max(1500, (s.phaseEndsAt! - conn!.now()) * .36))], ['pick']]);
    // the pick box fills in from updateStage, and changes when the pick comes in
    $('#stage').innerHTML = `<div class="stand ${many ? 'many' : ''}"><div><div class="ctx">${t('End of round {n} of {m}', { n: sd.round, m: sd.of })}</div><h1>${t('Standings')}</h1><div class="srows ${many ? 'many' : ''}" style="--k:${Math.ceil(sd.rows.length / 2)}">${shead}${many ? shead : ''}${rows}</div></div><div class="pickbox" id="pickBox"></div></div><div class="nextin" id="nextIn"></div>`;
  } else if (s.phase === 'gameover') {
    const rk = ranked(s.players), top = rk.filter((p) => p.pos === 1), w = top[0], second = rk.find((p) => p.pos > 1);
    const name = !w ? '' : top.length > 2 ? t("It's a tie!") : top.length === 2 ? t('{a} and {b} win!', { a: esc(top[0].name), b: esc(top[1].name) }) : t('{name} wins!', { name: esc(w.name) });
    const line = !w ? '' : top.length > 1 ? (w.score === 1 ? t('1 point each') : t('{n} points each', { n: w.score })) : second ? t('{n} points, {d} ahead of {name}', { n: w.score, d: w.score - second.score, name: esc(second.name) }) : w.score === 1 ? t('1 point') : t('{n} points', { n: w.score });
    const confetti = reducedMotion() ? '' : `<div class="confetti" aria-hidden="true">${Array.from({ length: 44 }, (_, i) => `<i style="left:${(i * 37) % 100}%;animation-delay:${((i * .13) % 1.6).toFixed(2)}s;background:${['#ff6a3d', '#3fb6ff', '#2ee6a6', '#ff5f9e', '#141432'][i % 5]};--r:${(i * 47) % 360}deg"></i>`).join('')}</div>`;
    const aws = (s.awards || []).length;
    st.innerHTML = w ? `<div class="final"><div class="burst"></div>${confetti}
        <div class="fw"><div class="hero">${avatar({ ...w, crown: true }, 'grin', 'av-show')}</div>
        <h1 class="w" data-fit="140" data-lines="1">${name}</h1>
        ${top.length > 2 ? `<div class="ties">${top.map((p) => avatar(p, 'grin')).join('')}</div>` : ''}
        <div class="pts">${line}</div></div>
        ${aws ? `<div class="fa"><h1>${t('Awards')}</h1>${awardsHtml(s)}</div>` : ''}
        <div class="fb"><h1>${t('Final board')}</h1>${tonightStrip(s)}${rk.length > 1 ? `<div class="board" style="${boardFit(rk.length, !!tonightStrip(s))}">${rk.map((p) => `<div class="card1 ${p.pos === 1 ? 'top' : ''}"><span class="n">${p.pos}</span>${avatar(p, p.pos === 1 ? 'grin' : 'smile')}<span class="cn">${esc(p.name)}</span><b>${p.score}</b></div>`).join('')}</div>` : ''}</div></div>`
      : `<div class="final"><h1 class="w">${t('Game over')}</h1></div>`;
    if (w) steps([['winner', 6000], ...(aws ? [['awards', 2500 + aws * 1500] as Step] : []), ['board']]);
  }
  setTimeout(focusFirst, 30);
}

// The final board: everyone on equal cards in an even grid, read down then across, as big as the screen holds: one column
// up to 4 players, two up to 10, then three and four. The cards' size (in rem) is the largest that fits both the
// columns' width (a card wants about 16.5 of its own units) and the rows' height (about 30rem under the title).
function boardFit(n: number, tally = false) {
  const cols = n <= 4 ? 1 : n <= 10 ? 2 : n <= 12 ? 3 : 4, rows = Math.ceil(n / cols);
  const colW = (74 - (cols - 1) * 1.4) / cols, k = Math.min(1.45, colW / 16.5, (tally ? 26.5 : 30) / (5.2 * rows - 1)); // (tonight's tally takes a line)
  return `--cols:${cols};--rows:${rows};font-size:${k.toFixed(3)}rem`;
}
// Tonight's tally, under the final board's title from the second game in this room: "Tonight, 3 games: Laura 2 · Ali 1".
function tonightStrip(s: GameView) {
  const n = s.tonight;
  if (!n || n.games < 2) return '';
  const won = n.rows.filter((r) => r.wins > 0);
  return `<div class="tonight"><span class="th">${esc(t('Tonight, {n} games', { n: n.games }))}</span>${won.slice(0, 6).map((r) => `<span class="tw">${esc(r.name)} <b>${r.wins}</b></span>`).join('<i>·</i>')}${won.length > 6 ? `<i>·</i><span class="tw">+${won.length - 6}</span>` : ''}</div>`;
}
// Between games, a line under the lobby's keys: tonight's wins so far, or (a new night) the house table of the players
// here who have played on this TV before. One or the other, in two short lines (the title, then up to four winners),
// so the cast below keeps its room.
function nightHtml(s: GameView) {
  const n = s.tonight, h = s.house;
  const line = (title: string, sub: string, rows: { name: string; wins: number }[]) => {
    const won = rows.filter((r) => r.wins > 0);
    return `<div class="night"><b class="nt">${esc(title)}</b><span class="ns">${esc(sub)}</span><i class="nbr"></i>${won.slice(0, 4).map((r) => `<span class="nw"><span>${esc(r.name)}</span> <b>${r.wins}</b></span>`).join('')}${won.length > 4 ? `<span class="nw">+${won.length - 4}</span>` : ''}</div>`;
  };
  if (n) return line(t('Tonight'), n.games === 1 ? t('1 game · wins') : t('{n} games · wins', { n: n.games }), n.rows);
  if (h) return line(t('The house table'), t('all-time wins'), h);
  return '';
}
// The end-of-party awards: up to three stickers, dealt in one by one under the board.
function awardsHtml(s: GameView) {
  if (!s.awards || !s.awards.length) return '';
  return `<div class="awards">${s.awards.map((a, i) => { const p = playerById(s, a.pid), [name, why] = awardText(a); return `<div class="aw" style="--i:${i}">${duo(AWARD_ICON[a.id] || 'trophy')}<span><b>${esc(name)}</b><span class="who">${avatar(p, 'grin')}<span>${esc(p.name)}</span></span><i>${esc(why)}</i></span></div>`; }).join('')}</div>`;
}
// The play screen is only there while a song is on air.
type OnAir = Extract<GameView, { phase: 'playing' | 'buzzed' }>;
function buildPlayHead(s: OnAir) {
  const tp = s.song.type, h = $('#playHead');
  const sub = (x: string) => x ? `<small>${x}</small>` : '';
  if (tp === 'drop') h.innerHTML = `<span class="variant">${duo('lightning')} ${t('Joker Drop')}</span><h1 class="q">${esc(t(s.question ? s.question.prompt : "What's the song called?"))}${sub(t('The fastest right answers win a joker.'))}</h1>`;
  else if (tp === 'fastest') h.innerHTML = `<h1 class="q">${esc(t(s.question!.prompt))}${sub(t('Answer on your phone. The fastest right answers score 3, 2 and 1.'))}</h1>`;
  else if (tp === 'year') h.innerHTML = `<h1 class="q">${t('What year is this from?')}${sub(t('Lock in a year on your phone.'))}</h1>`;
  else if (tp === 'timeline') h.innerHTML = `<h1 class="q">${t('Where does it go?')}${sub(t('Place it on the timeline on your phone. Right is 3 points.'))}</h1>`;
  else if (tp === 'twisted') h.innerHTML = `<span class="variant">${esc(t(s.song.variant!.label))}</span><h1 class="q">${t('Buzz when you know it')}</h1>`;
  else if (tp === 'snippet') h.innerHTML = `<h1 class="q">${t('Buzz when you know it')}${sub(t("The earlier you buzz, the more it's worth."))}</h1>`;
  else if (tp === 'lyrics') h.innerHTML = `<h1 class="q">${t('Finish the lyric')}${sub(t('Listen: the music stops just before the next line.'))}</h1>`;
  else if (tp === 'medley') h.innerHTML = `<h1 class="q">${t('Golden Medley')}${sub(t('Pick each title on your phone. The last songs are worth the most.'))}</h1>`;
  else if (tp === 'hum') {
    const p = s.hum && s.hum.pid ? playerById(s, s.hum.pid) : null;
    h.innerHTML = p ? `<div class="humhead">${avatar(p, 'shout', 'hero')}<h1 class="q">${esc(t('{name} is humming!', { name: p.name }))}${sub(t('No words allowed. Guess the song on your phone.'))}</h1></div>` : `<h1 class="q">${t('Hum It')}</h1>`;
  }
  else h.innerHTML = `<h1 class="q">${t('Buzz when you know it')}${sub(s.settings.judge === 'host' ? t('Then say the title and the artist out loud.') : t('Then pick the song on your phone.'))}</h1>`;
  // a Gold Record keeps its sticker while it plays
  if (s.gold) h.insertAdjacentHTML('afterbegin', `<span class="variant gold">${duo('gold-record')} ${esc(t('Gold Record · +{n}', { n: s.gold }))}</span>`);
}

// The shared timeline: cream cards in year order, the newest one in ink at the reveal.
function timelineHtml(tl: TimelineCard[], placedAt: number | null | undefined) {
  return `<div class="tl ${tl.length > 7 ? 'small' : ''}"><div class="tl-row">${tl.map((c, i) =>
    `<div class="tc ${i === placedAt ? 'new' : ''}"><span class="y">${c.year}</span><b>${esc(c.title)}</b><i>${esc(c.artist)}</i></div>`).join('')}</div></div>`;
}
// The Joker Drop's reveal: who snatched which joker (the fastest, and the fastest from the bottom half).
function dropWinners(s: GameView, won: DropWin[]) {
  if (!won.length) return `<div class="res"><div class="r1"><b>${t('Nobody got it: no jokers this time')}</b></div></div>`;
  return `<div class="dropwin">${won.map((w) => { const p = playerById(s, w.pid); return `<div class="dw">${avatar(p, 'grin')}<b>${esc(p.name)}</b><span class="tk">+${esc(jokerName(w.kind))}</span><i>${w.why === 'fastest' ? t('fastest of all') : t('fastest comeback')}</i></div>`; }).join('')}</div>`;
}
// The reveal's second step: everyone's points on this song, how each number was reached, and where it leaves them.
function pointsStep() {
  const s = S; if (!s || s.phase !== 'reveal' || !s.reveal) return;
  const rs = s.reveal.results, rows = rs.filter((x) => x.pts || x.stolenBy), none = s.players.filter((p) => !rows.some((x) => x.pid === p.pid)), many = rows.length > 6;
  const head = `<div class="prh"><span></span><span>${t('This song')}</span><span>${t('Total')}</span></div>`;
  const row = (x: ResultRow, i: number) => {
    const p = playerById(s, x.pid), to = p.score!, mood = x.pts > 0 ? 'grin' : x.pts < 0 || x.stolenBy ? 'sad' : 'smile';
    return `<div class="pr" style="--i:${i}">${avatar(x.stole && x.stole.pts ? { ...p, mask: true } : p, mood)}<span class="pn"><b>${esc(p.name)}</b><i>${esc(pointsWhy(s, x))}</i></span><span class="pg ${x.pts < 0 ? 'neg' : x.pts ? '' : 'zero'}">${x.pts > 0 ? '+' : ''}${x.pts}</span><span class="pt" data-to="${to - x.pts}">${to - x.pts}</span></div>`;
  };
  $('#stage').innerHTML = `<div class="ptsv ${many ? 'many' : ''}"><h1>${t('Points for this song')}</h1><div class="prs ${many ? 'many' : ''}" style="--k:${Math.ceil(rows.length / 2)}">${head}${many ? head : ''}${rows.map(row).join('')}</div>${none.length ? `<p class="pnone">${esc(t('No points: {names}', { names: none.map((p) => p.name).join(', ') }))}</p>` : ''}</div><div class="nextin" id="nextIn"></div>`;
  $('#nextIn').dataset.last = s.round >= s.total ? '1' : '';
  setStep('points');
  fitStage();
  // the totals count up once the points have landed
  setTimeout(() => { if (S && S.phase === 'reveal') document.querySelectorAll<HTMLElement>('.pr .pt').forEach((el, i) => setScore(el, playerById(S!, rows[i].pid).score!, true)); }, 900);
}
const optRows = (opts: QuestionOption[], hide: number[] = [], cls = '') => `<div class="cards ${cls}">${opts.map((o, i) => `<div class="card ${hide.includes(i) ? 'gone' : ''}" style="--i:${i}"><span class="k">${'ABCD'[i]}</span>${trackHtml(o)}</div>`).join('')}</div>`;

// The round intro has to fit between the header and the cast: the title shrinks, then the how-to gives way.
function fitIntro() {
  const el = $('.intro'), h = el && $('h1', el); if (!h) return;
  const kids = [...el.children as HTMLCollectionOf<HTMLElement>].filter((c) => !c.classList.contains('disc') && c.offsetHeight);
  const used = () => kids[kids.length - 1].getBoundingClientRect().bottom - kids[0].getBoundingClientRect().top;
  const fits = () => used() <= el.clientHeight - 20 && h.scrollWidth <= h.clientWidth + 1;
  // A new intro starts at full size; later calls (the jokers and stickers filling in) only ever shrink it.
  let size = parseFloat(h.style.fontSize) || 9.5; h.style.fontSize = size + 'rem';
  while (!fits() && size > 5) { size -= .25; h.style.fontSize = size + 'rem'; }
  if (!fits() && !el.classList.contains('tight')) { el.classList.add('tight'); while (fits() && size < 9.5) { size += .25; h.style.fontSize = size + 'rem'; } if (!fits()) { size -= .25; h.style.fontSize = size + 'rem'; } }
  while (!fits() && size > 4) { size -= .25; h.style.fontSize = size + 'rem'; }
  h.style.fontSize = size + 'rem';
  if (!el.dataset.watched && window.ResizeObserver) { (el as NumberText).dataset.watched = 1; const ro = new ResizeObserver(() => requestAnimationFrame(fitIntro)); kids.filter((k) => k !== h).forEach((k) => ro.observe(k)); ro.observe(el); }
}
if (document.fonts) document.fonts.ready.then(fitIntro);
addEventListener('resize', fitIntro);
function renderPlays(s: GameView) {
  const el = $('#plays'); if (!el) return;
  const sig = JSON.stringify([s.jokerWindow, s.upNext, s.gold, LANG]);
  if (el.dataset.sig === sig) return;
  el.dataset.sig = sig;
  el.innerHTML = (s.gold ? `<div class="tags">${goldHtml(s)}${upNextHtml(s)}</div>` : upNextHtml(s)) + (s.jokerWindow ? `<div class="ph"><span>${t('Jokers')}</span><span>${t('Play one on your phone now')}</span></div>` : '');
}

// What a joker just did, for the room: a banner over the cast's heads says who played it, on whom, and what it does. One at a
// time; when several come at once each moves on sooner, and one that waited too long is dropped (the jokers played are
// listed on screen anyway). A Steal is only told at the reveal; a 50/50 played by the buzzer is on the buzz screen.
interface Banner { kind: JokerKind; cls?: string; who: KnownPlayer; mood?: Mood; title: string; sub?: string; at: number }
const bq: Banner[] = [];
let bShown = 0, bTimer = 0, bRevealed: number | null = null, bRound: number | null = null;
function banner(b: Omit<Banner, 'at'>) {
  bq.push({ ...b, at: Date.now() });
  if (!bShown) return nextBanner();
  clearTimeout(bTimer);
  bTimer = setTimeout(nextBanner, Math.max(0, 1800 - (Date.now() - bShown)));
}
function nextBanner() {
  clearTimeout(bTimer);
  let box = $('#jbanners');
  if (!box) { box = document.createElement('div'); box.id = 'jbanners'; box.setAttribute('aria-live', 'polite'); document.body.appendChild(box); }
  // a wrong answer's toast has the band to itself: the banners wait for it to go
  if (S && S.phase === 'playing' && S.lastWrong) { box.innerHTML = ''; bShown = 0; if (bq.length) bTimer = setTimeout(nextBanner, 300); return; }
  while (bq.length && Date.now() - bq[0].at > 8000) bq.shift();
  const b = bq.shift();
  if (!b) { box.innerHTML = ''; bShown = 0; return; }
  // in the band over the cast: clear of the characters' badges, which reach a little above their heads
  const st = $('#stage').getBoundingClientRect(), rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
  box.style.bottom = (st.height ? innerHeight - st.bottom + rem * .55 : innerHeight * .06) + 'px';
  box.innerHTML = `<div class="jban ${b.cls || ''}">${avatar(b.who, b.mood || 'grin')}<span class="stub">${esc(jokerName(b.kind))}</span><span><b>${esc(b.title)}</b>${b.sub ? `<small>${esc(b.sub)}</small>` : ''}</span></div>`;
  bShown = Date.now();
  bTimer = setTimeout(nextBanner, bq.length ? 1800 : 3200);
}
// a joker as the show reports it (the fx) or as the song keeps it (s.jokerPlays)
interface JokerNews { pid: string; kind: JokerKind; target?: string | null }
function jokerBanner(s: GameView, m: JokerNews) {
  const who = playerById(s, m.pid), nm = (pid: string) => playerById(s, pid).name, atCut = s.segment && s.segment.type === 'lyrics';
  if (m.kind === 'freeze') banner({ kind: 'freeze', cls: 'j-ice', who, title: t('{name} froze {target}', { name: who.name, target: nm(m.target!) }),
    sub: atCut ? t('{n} seconds on ice when the answers open', { n: SongBuzzEngine.FREEZE_SECS }) : t('{n} seconds on ice when the song starts', { n: SongBuzzEngine.FREEZE_SECS }) });
  else if (m.kind === 'double') banner({ kind: 'double', cls: 'j-sun', who, title: t('{name} goes ×2', { name: who.name }), sub: s.segment && s.segment.type === 'medley' ? t('Their best song of the six counts twice') : t('Double or nothing: points and losses count twice') });
  else if (m.kind === 'fifty' && s.phase !== 'buzzed') banner({ kind: 'fifty', who, title: t('{name} played 50/50', { name: who.name }),
    sub: ['year', 'timeline'].includes((s.song && s.song.type)!) ? t('Their phone shows the decade') : t('Two wrong answers gone from their phone') });
}
// at the reveal, the Steals come out; between rounds and after the game there's no cast to sit on, and a banner goes
// with its song: pressing Next on a reveal leaves none of them to land on the next song's jokers
function jokerNewsTv(s: GameView) {
  if (s.round !== bRound || !['intro', 'countdown', 'drop', 'playing', 'buzzed', 'reveal'].includes(s.phase)) { bRound = s.round; bq.length = 0; if (bShown) nextBanner(); }
  if (s.phase !== 'reveal' || !s.reveal || bRevealed === s.round) return;
  const fresh = lastPhase !== null; // a TV opened on a reveal doesn't announce it
  bRevealed = s.round;
  bq.length = 0; if (bShown) nextBanner(); // the song's ×2s and freezes are done with: only its Steals come out here
  if (!fresh) return;
  const steals = (s.reveal.results || []).filter((y) => y.stole).slice(0, 3) as Steal[];
  if (steals.length) setTimeout(() => { if (S && S.phase === 'reveal') stealNews(s, steals); }, 1200);
}
type Steal = ResultRow & { stole: StolenPoints };
function stealNews(s: GameView, steals: Steal[]) {
  for (const x of steals) {
    const who = playerById(s, x.pid), target = playerById(s, x.stole.from).name;
    if (x.stole.refund) banner({ kind: 'steal', cls: 'j-rob', who, mood: 'sad', title: t('{name} tried to rob {target}', { name: who.name, target }), sub: t('Nothing to steal: the card goes back to {name}', { name: who.name }) });
    else banner({ kind: 'steal', cls: 'j-rob', who, title: t('{name} stole {n} from {target}!', { name: who.name, n: x.stole.pts, target }), sub: t('A secret Steal, played before the song') });
  }
}

// Between rounds: whoever is last picks the next round from two, live; before the finale it's just what's next.
function renderPick(s: GameView) {
  const el = $('#pickBox'), sd = s.standings; if (!el || !sd) return;
  const sig = JSON.stringify([sd.picker, sd.picked, LANG]);
  if (el.dataset.sig === sig) return;
  el.dataset.sig = sig;
  const card = (label: string, cls = '', tick = '') => { const c = GENRE_COLOR[label] || '#2a4bff'; return `<div class="rc ${cls}" style="--c:${c};--t:${isLight(c) ? INK : CREAM}">${icon('note')}<b>${esc(genreLabel(label, s.settings))}</b>${tick}</div>`; };
  const p = playerById(s, sd.picker);
  // everyone level: nobody is last, it was drawn; two players: the other one is behind more than they're last
  const pickWhy = sd.level ? (sd.twist ? 'All square, so {name} was drawn to pick: new music or a twist' : 'All square, so {name} was drawn to pick the music for the next round')
    : duel(s) ? (sd.twist ? "{name} is behind, so they pick: new music or a twist" : "{name} is behind, so they pick the music for the next round")
    : sd.twist ? '{name} is last, so they pick: new music or a twist' : '{name} is last, so they pick the music for the next round';
  const who = sd.picked === 'twist' ? t('{name} picked the twist', { name: esc(p.name) }) : sd.picked ? t('{name} picked the music', { name: esc(p.name) }) : t(pickWhy, { name: esc(p.name) });
  const cls = (x: string) => (!sd.picked ? '' : sd.picked === x ? 'on' : 'off');
  // the twist, in place of a third music: an ink card with what it does
  const tw = sd.twist ? twistText(sd.twist) : null;
  const twCard = tw ? `<div class="rc tw ${cls('twist')}">${duo(sd.twist === 'double' ? 'lightning' : 'sunglasses')}<span><b>${esc(tw[0])}</b><small>${esc(tw[1])}</small></span>${sd.picked === 'twist' ? `<span class="tick">${t('Picked')}</span>` : ''}</div>` : '';
  el.innerHTML = `<div class="who">${avatar(p, sd.picked ? 'grin' : 'shout')}<span>${who}</span></div>
    <div class="lbl">${t('Next: {round}', { round: esc(typeTitle(sd.next)) })}</div>
    <div class="rcs">${sd.choices.map((x) => card(x, cls(x), sd.picked === x ? `<span class="tick">${t('Picked')}</span>` : '')).join('')}${twCard}</div>`;
}
let countrySent = false, voiceSent = '';
// Another house's game on this screen: type its code, here or on the way back from a code that wasn't right.
const watchForm = () => `<form id="watchForm" class="watchform"><label class="sr" for="watchCode">${t('Room code')}</label><input class="field code" id="watchCode" maxlength="4" autocomplete="off" autocapitalize="characters" spellcheck="false" enterkeyhint="go" placeholder="ABCD"><button class="btn go">${t('Join')}</button></form>`;
function bindWatchForm() {
  const code = $<HTMLInputElement>('#watchCode');
  $('#watchForm').onsubmit = (e) => {
    e.preventDefault();
    const v = code.value.toUpperCase().replace(/[^A-Z]/g, '');
    if (v.length !== 4) return code.focus();
    const q = new URLSearchParams(location.search); q.set('r', v); location.search = q.toString();
  };
  code.focus();
}
function noRoomHere(code: string, why = t('No game with the code {code}. Check the TV and try again.', { code: esc(code) })) {
  $('#stage').innerHTML = `<div class="nowatch"><div class="error">${icon('cross')} ${why}</div>${watchForm()}<button class="linkish" id="watchOut">${t('Start a game on this screen')}</button></div>`;
  $('#watchOut').onclick = leaveRoom;
  bindWatchForm();
}
function leaveRoom() { const q = new URLSearchParams(location.search); q.delete('r'); location.search = q.toString(); }
// the lobby's title: who's playing, everyone's in (all ready), or the options while they're open
function lobbyTitle(s: GameView) {
  const el = $('#lobbyTitle'); if (!el) return;
  const here = s.players.filter((p) => p.online !== false);
  const title = document.querySelector('#settings .custom') ? t('Change the show') : here.length && here.every((p) => p.ready) ? t("Everyone's in!") : t("Who's playing?");
  if (el.textContent !== title) el.textContent = title;
}
function updateStage(s: GameView) {
  if (s.phase === 'intro' || s.phase === 'countdown') { renderPlays(s); fitIntro(); }
  if (s.phase === 'standings') renderPick(s);
  if (s.phase === 'lobby') {
    // the show learns where the TV is once (Today's hits, local music); Customize can change it
    // where the TV is: tonight's country unless the host picks another (and then that one is theirs: engine hostCountry)
    if ((!s.settings.country || !s.settings.home) && !countrySent) { countrySent = true; const cc = store.get('sb_home') || detectCountry(); conn!.send({ t: 'settings', patch: { home: cc, ...(s.settings.country ? {} : { country: cc }) } }); }
    // the presenter speaks the language of the TV that runs the room, on every screen in it (a follower TV only listens)
    if (!WATCH && s.settings.voice !== LANG && voiceSent !== LANG) { voiceSent = LANG; conn!.send({ t: 'settings', patch: { voice: LANG } }); }
    // where the host says this TV is, unlike where it guessed, holds for its next rooms too
    else if (!WATCH && s.settings.home && s.settings.home !== store.get('sb_home') && (store.get('sb_home') || s.settings.home !== detectCountry())) store.set('sb_home', s.settings.home);
    if (!WATCH) renderSettings($('#settings'), s, (m) => conn!.send(m), { brief: true, start: t('Start the show'), onStart: () => conn!.send({ t: 'start' }) });
    else if (!$('#watchOut')) { $('#settings').innerHTML = `<p class="watching">${t('Connected to room {code} · Waiting for the host to start', { code: esc(WATCH) })}</p><button class="linkish" id="watchOut">${t('Leave this room')}</button>`; $('#watchOut').onclick = leaveRoom; }
    const nb = $('#nightBox'), nv = nightHtml(s);
    if (nb && nb.dataset.v !== nv) { nb.dataset.v = nv; nb.innerHTML = nv; }
    $('#lobbyErr').innerHTML = s.error ? `<div class="error">${icon('cross')} ${esc(tMsg(s.error))}</div>` : '';
    // who's here: the players connected now (one whose phone went away is counted apart)
    const n = s.players.filter((p) => p.online !== false).length, away = s.players.length - n, vip = s.vip && playerById(s, s.vip);
    // ready: answered or skipped their music on the phone (engine.ts prefs); then the host is called to start
    const here = s.players.filter((p) => p.online !== false), all = here.length > 0 && here.every((p) => p.ready), host = vip && vip.online !== false ? vip : null;
    lobbyTitle(s);
    const hint = !s.players.length ? t('Waiting for the first player to scan the code')
      : all ? (host ? t("Everyone's ready! {name}, press Start on your phone", { name: host.name }) : t("Everyone's ready! Press Start"))
      : (n === 1 ? t('1 player in') : t('{n} players in', { n })) + (away ? ' · ' + t('{n} away', { n: away }) : '') + (host ? ' · ' + t('{name} starts the show from their phone', { name: host.name }) : '');
    const hel = $('#startHint'), hsig2 = hint + (host ? host.pid : '');
    if (hel.dataset.sig !== hsig2) { hel.dataset.sig = hsig2; hel.innerHTML = (all && host ? avatar(host, 'shout') : '') + `<span>${esc(hint)}</span>`; }
    hel.classList.toggle('go', all);
    const hostUrl = s.hostUrl || s.joinUrl.replace(/\/play$/, '/host');
    const hj = $('#hostJoin'), hsig = s.settings.judge === 'host' && !WATCH ? hostUrl + s.hostConnected : '';
    if (hj.dataset.sig !== hsig) {
      hj.dataset.sig = hsig;
      hj.innerHTML = !hsig ? '' : s.hostConnected ? `<p class="hostline"><span class="lamp green on"></span> ${t('Quizmaster connected')}</p>`
        : `<div class="hostplate"><img src="${esc(apiUrl('/qr.svg?u=' + encodeURIComponent(hostUrl)))}" alt=""><div><b>${t('Quizmaster')}</b><span>${t("Whoever runs the show scans this. They see the answers, so they don't play.")}</span></div></div>`;
    }
  }
  if (s.phase === 'playing' || s.phase === 'buzzed') {
    const tp = s.song.type, body = $('#playBody');
    const sig = JSON.stringify([tp, s.song.stage, audioErr, s.lyric && s.lyric.open, s.medley && s.medley.i]);
    // the cassette steps aside when the screen needs the room: the lyric's four answers, the medley's dots and answers
    // Finish the Lyric keeps its cassette in the answers' place until they open, so the line never moves
    const play = $('#stage .play'); if (play) play.classList.toggle('notape', tp === 'medley' || tp === 'lyrics');
    if (body.dataset.sig !== sig) {
      body.dataset.sig = sig;
      let h = '';
      if ((tp === 'fastest' || tp === 'hum' || tp === 'drop') && s.question) h = optRows(s.question.options!);
      else if (tp === 'lyrics' && s.lyric) {
        const open = s.lyric.open && s.question;
        h = `<div class="lyr"><p class="prev">“${esc(s.lyric.prev)}”</p><p class="blanks">${'<i></i>'.repeat(Math.min(14, s.lyric.words))}</p></div><div class="lyrbox">${optRows(Array.from({ length: 4 }, () => ({ title: '\u00a0' })), [], 'ghost')}${open ? optRows(s.question!.options!) : tapeSvg()}</div>`;
      }
      else if (tp === 'medley' && s.medley) {
        const w = s.medley.pts ? s.medley.pts[s.medley.i] : 1;
        h = `<div class="mdots">${Array.from({ length: s.medley.n }, (_, i) => `<i class="${i < s.medley!.i ? 'done' : i === s.medley!.i ? 'now' : ''}">${i + 1}</i>`).join('')}</div><span class="mworth">${w === 1 ? t('Worth 1 point') : t('Worth {n} points', { n: w })}</span>${s.medley.options ? optRows(s.medley.options) : ''}`;
      }
      else if (tp === 'timeline' && s.timeline) h = timelineHtml(s.timeline, null);
      else if (tp === 'snippet') h = `<div class="stages">${s.song.stages.map((sec, i) => { const pts = s.song.stagePts[i] * s.segment!.mult; return `<div class="stage1 ${i < s.song.stage ? 'done' : i === s.song.stage ? 'now' : ''}"><b>${sec}s</b><span>${pts === 1 ? t('1 point') : t('{n} points', { n: pts })}</span></div>`; }).join('')}</div>`;
      if (audioErr) h += `<div class="audio-warn">${icon('sound')} ${esc(audioErr)}</div>`;
      body.innerHTML = h;
    }
    // after a wrong answer the buzzers stay shut for a moment while the song plays on: the room listens again
    const listening = s.phase === 'playing' && s.song.opensAt && conn!.now() < s.song.opensAt;
    if (s.lastWrong && bShown) nextBanner(); // (it holds the banners while it's up)
    $('#toastBox').innerHTML = s.lastWrong ? `<div class="toast">${avatar(playerById(s, s.lastWrong.pid), 'sad')}${esc(t('{name} got it wrong', { name: playerById(s, s.lastWrong.pid).name }))}${listening ? `<span class="listen">${icon('headphones')}${esc(t('Listen…'))}</span>` : ''}</div>` : '';
    const f = $('#flash');
    if (s.phase === 'buzzed' && s.buzz) {
      const p = playerById(s, s.buzz.pid);
      const fsig = s.song.key + s.buzz.pid + !!s.buzzFifty + LANG;
      if (f.dataset.sig !== fsig) {
        f.dataset.sig = fsig;
        const tk = takeover(legendOf(p.outfit)); // a Legend's buzz takes the whole screen
        f.style.setProperty('--c', tk ? tk.field : p.color);
        f.classList.toggle('light', isLight(tk ? tk.field : p.color));
        f.classList.toggle('legend', !!tk);
        const dbl = (s.jokerPlays || []).some((x) => x.pid === s.buzz!.pid && x.kind === 'double');
        const rivals = s.players.filter((x) => x.pid !== p.pid && x.online !== false);
        f.innerHTML = `${tk ? `<div class="takeover">${tk.art}</div>` : '<div class="burst"></div>'}<div class="ring"></div><div class="rivals ${rivals.length > 7 ? 'many' : ''}">${rivals.slice(0, 12).map((x, i) => avatar(x, 'gasp').replace('style="', `style="--i:${i};`)).join('')}</div><div class="inner"><div class="logo">SongBuzz</div><div class="clockc" id="buzzSecs">${secsLeft(conn!, s.buzz.deadline)}</div>
          ${avatar(p, 'shout', 'hero av-show')}<div class="bz" data-fit="120" data-lines="1">${esc(t('{name}!', { name: p.name }))}</div>
          <div class="sub">${s.settings.judge === 'host' ? t('Buzzed first: say the title and the artist') : t('Buzzed first and is picking on the phone')}${dbl ? ` <span class="stub">${t('×2 riding on it')}</span>` : ''}${s.buzzFifty ? ' <span class="stub">50/50</span>' : ''}</div></div>`;
        f.dataset.refit = '1';
      }
      f.classList.add('flashon');
      // fitted once it shows: hidden, everything measures 0 and a long name stayed full size, on two lines
      if (f.dataset.refit) { f.dataset.refit = ''; requestAnimationFrame(() => fitFlaps(f)); }
    } else { f.classList.remove('flashon'); f.dataset.sig = ''; }
  }
}

// The cast: every player as a character along the bottom, in chart order, hopping to their new place. They dance while
// the song plays at the reveal, and a player who joins pops in (not the ones already there when the TV opens).
let castDrawn = false;
// An element the game writes plain numbers to: the DOM turns them into text.
interface NumberText { textContent: string | number | null; dataset: Record<string, string | number | undefined>; classList: DOMTokenList }
function setScore(el: NumberText, to: number, tween: boolean) {
  if (el.dataset.to === String(to)) return;
  el.dataset.to = to;
  const from = +el.textContent!;
  if (from === to) return;
  if (!tween || reducedMotion()) { el.textContent = to; return; }
  const t0 = performance.now(), dur = 650;
  el.classList.add('tick');
  const step = (now: number) => {
    if (el.dataset.to !== String(to)) return;
    const k = Math.min(1, (now - t0) / dur);
    el.textContent = Math.round(from + (to - from) * k);
    if (k < 1) requestAnimationFrame(step); else el.classList.remove('tick');
  };
  requestAnimationFrame(step);
}
// The cast's row: every child is a character's element.
interface CastRows extends HTMLElement { readonly children: HTMLCollectionOf<HTMLElement> }
function renderChart(s: GameView) {
  const el = $('#chart');
  const lobby = s.phase === 'lobby';
  if (!el.dataset.built) { el.dataset.built = '1'; el.innerHTML = `<div class="rows" id="rows"></div>`; }
  const rowsEl = $<CastRows>('#rows');
  // smaller as the cast grows and keeps clear of the corners: one line up to 10 players, two lines for a bigger room
  const n = s.players.length, two = n > 10;
  rowsEl.classList.toggle('compact', n > 6 && !two);
  rowsEl.classList.toggle('two', two);
  document.body.classList.toggle('bigcast', two);
  rowsEl.style.setProperty('--per', String(Math.max(1, two ? Math.ceil(n / 2) : n)));
  const here = s.players.filter((p) => p.online !== false);
  rowsEl.classList.toggle('av-dance', s.phase === 'reveal' || (lobby && here.length > 0 && here.every((p) => p.ready)));
  // the reveal keeps its secret: the cast stands in the order and with the scores it came in with, and moves on after
  const held = s.phase === 'reveal' && s.reveal ? new Map(s.reveal.results.map((x) => [x.pid, x.pts])) : null;
  const list = lobby ? s.players.map((p, i) => ({ ...p, pos: i + 1 })) : ranked(held ? s.players.map((p) => ({ ...p, score: p.score - (held.get(p.pid) || 0) })) : s.players);
  const inSong = ['intro', 'countdown', 'drop', 'playing', 'buzzed'].includes(s.phase);
  const playMap = new Map(shownPlays(s).map((x) => [x.pid, x.kind]));
  const frozenSet = new Set(s.frozenPids || []);
  const before = new Map([...rowsEl.children].map((r) => { const b = r.getBoundingClientRect(); return [r.dataset.pid, [b.left, b.top]]; }));
  const keep = new Set();
  list.forEach((p, i) => {
    keep.add(p.pid);
    let row = rowsEl.querySelector<HTMLElement>(`[data-pid="${CSS.escape(p.pid)}"]`);
    if (!row) { row = document.createElement('div'); row.dataset.pid = p.pid; }
    const locked = s.locked && s.locked.includes(p.pid) && ['playing', 'buzzed'].includes(s.phase);
    const blocked = inSong && frozenSet.has(p.pid);
    const buzzing = s.buzz && s.buzz.pid === p.pid && s.phase === 'buzzed';
    const cls = ['row'];
    if (!p.online) cls.push('off');
    if (locked || blocked) cls.push('out');
    if (buzzing) cls.push('buzzing');
    if (!lobby && p.pos === 1 && p.score > 0) cls.push('lead');
    if (s.phase === 'gameover' && p.pos !== 1) cls.push('dim');
    if (blocked) cls.push('iced');
    const tag = itemOf('tag', p.outfit && p.outfit.tag); if (tag) cls.push('tag-' + tag.id);
    row.className = cls.join(' ');
    const answered = s.answered && s.answered.includes(p.pid);
    const mood = buzzing ? 'shout' : !p.online && !lobby ? 'sleep' : locked || blocked ? 'sad'
      : s.phase === 'countdown' ? 'ready' : s.phase === 'playing' && !answered ? 'listen' : 'smile';
    let badge = '';
    // in the lobby: who's still picking (the line under the programme says who starts it)
    if (lobby && p.online !== false && !p.ready) badge = `<span class="badge pk wait">${t('Picking…')}</span>`;
    else if (lobby && p.pid === s.vip) badge = `<span class="badge ok">${t('Host')}</span>`;
    else if (!p.online) badge = `<span class="badge">${t('Away')}</span>`;
    else if (s.hum && s.hum.pid === p.pid && ['countdown', 'playing'].includes(s.phase)) badge = `<span class="badge ok">${t('Humming')}</span>`;
    else if (locked && !['reveal', 'gameover'].includes(s.phase)) badge = `<span class="badge bad">${t('Out')}</span>`;
    else if (blocked) badge = `<span class="badge ice">${t('Frozen')}</span>`;
    else if (s.answered && s.answered.includes(p.pid) && s.phase === 'playing') badge = `<span class="badge ok">${duo('check-fat')}</span>`;
    else if (inSong && playMap.has(p.pid)) badge = `<span class="badge">${esc(jokerName(playMap.get(p.pid)!))}</span>`;
    // drawn again only when something shows: a new drawing would start the character's moves from the top
    // in the lobby, the best thing they earned, where the score goes in the game: what there is to earn, by playing
    const prize = lobby ? prizeOf(p.outfit) : undefined;
    const sig = [p.name, p.color, p.species, JSON.stringify(p.outfit), p.clay, prize && prize.id, p.streak >= SongBuzzEngine.STREAK, mood, blocked, badge, s.players.length > 10].join('|');
    if (row.dataset.sig !== sig) {
      row.dataset.sig = sig;
      row.innerHTML = `${badge}${avatar(p, mood, [blocked ? 'av-frozen' : '', s.players.length > 10 ? '' : 'av-show'].join(' '))}<span class="nm">${esc(p.name)}</span><span class="pts">${p.score}</span>${prize ? `<span class="worn">${esc(t(prize.name))}</span>` : ''}`;
    }
    setScore($<HTMLElement>('.pts', row), p.score, lastPhase === 'reveal');
    if (rowsEl.children[i] !== row) rowsEl.insertBefore(row, rowsEl.children[i] || null);
  });
  [...rowsEl.children].forEach((r) => { if (r.dataset.pid && !keep.has(r.dataset.pid)) r.remove(); });
  [...rowsEl.children].forEach((r) => {
    const was = before.get(r.dataset.pid), still = reducedMotion();
    if (was == null) {
      if (castDrawn && r.dataset.pid && !still) r.animate([{ transform: 'scale(0) rotate(-20deg)' }, { transform: 'scale(1.15) rotate(6deg)', offset: .55 }, { transform: 'scale(.95) rotate(-2deg)', offset: .8 }, { transform: 'none' }], { duration: 600, easing: 'ease-out' });
      return;
    }
    const b = r.getBoundingClientRect(), dx = was[0] - b.left, dy = was[1] - b.top; if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
    // a real change of place is a hop; a nudge (someone else joined or left) just slides
    const hop = Math.abs(dx) > b.width / 2 && !still ? [{ transform: `translate(${dx / 2}px, ${dy / 2}px) translateY(-1.6rem)`, offset: .45 }] : [];
    r.animate([{ transform: `translate(${dx}px, ${dy}px)` }, ...hop, { transform: 'none' }], { duration: 800, easing: hop.length ? 'ease-in-out' : 'cubic-bezier(.16,1,.3,1)' });
  });
  if (s.players.length) castDrawn = true;
  // the cast jumps for the new round; two lines of players bounce in place, so no one lands on the scores of the line above
  const jump = rowsEl.classList.contains('two') ? 'scale(1.08, .92)' : 'translateY(-2.2rem) rotate(-4deg)';
  if (s.phase === 'intro' && lastPhase !== 'intro' && !reducedMotion()) [...rowsEl.children].forEach((r, i) => r.animate([{ transform: 'none' }, { transform: jump, offset: .45 }, { transform: 'translateY(.2rem)', offset: .8 }, { transform: 'none' }], { duration: 620, delay: 200 + i * 90, easing: 'ease-in-out' }));
  const empty = rowsEl.querySelector('.chart-empty');
  if (!s.players.length && !empty) rowsEl.insertAdjacentHTML('beforeend', `<div class="chart-empty">${t('Nobody here yet. Scan the code with your phone and pick a name.')}</div>`);
  if (s.players.length && empty) empty.remove();
  fitNames(el);
}
// A name longer than its place narrows its letters (Archivo has a width axis) before it is ever cut short. A cast or a
// board narrows together, so its names stay one family (to 76% at most); a name still too long then narrows on its own,
// and only past that is it cut. Measured again only when the names or their room change.
function fitNames(root: ParentNode = document) {
  root.querySelectorAll<HTMLElement>('#rows, .final .board').forEach((g) => {
    const names = [...g.querySelectorAll<HTMLElement>('.row .nm, .card1 .cn')];
    const room = names.map((n) => (n.parentElement as HTMLElement).clientWidth);
    const sig = names.map((n) => n.textContent).join('|') + '|' + room.join(',');
    // (a character drawn again brings a new name, not yet fitted)
    if (!names.length || !room[0] || (g.dataset.nfit === sig && names.every((n) => n.dataset.nk))) return;
    g.dataset.nfit = sig;
    names.forEach((n) => { n.dataset.nk = '1'; });
    const over = () => names.filter((n) => n.scrollWidth > n.clientWidth + 1);
    names.forEach((n) => { n.style.fontStretch = ''; });
    let k = 100, o = over();
    while (o.length && k > 76) { k -= 6; names.forEach((n) => { n.style.fontStretch = k + '%'; }); o = over(); }
    while (o.length && k > 64) { k -= 6; o.forEach((n) => { n.style.fontStretch = k + '%'; }); o = over(); }
  });
}
addEventListener('resize', () => fitNames());
if (document.fonts) document.fonts.ready.then(() => { document.querySelectorAll<HTMLElement>('[data-nfit]').forEach((e) => { e.dataset.nfit = ''; }); fitNames(); });

let endArmed = 0;
function renderCtrls(s: GameView) {
  if (WATCH) return;
  const c = $('#ctrls');
  const sig = s.phase + (s.paused ? ':paused' : '');
  if (c.dataset.sig === sig) return;
  c.dataset.sig = sig;
  const keep = document.activeElement && c.contains(document.activeElement) ? document.activeElement.id : null;
  let h = '';
  if (s.phase === 'reveal' || s.phase === 'standings') h = `<button id="nextBtn" class="btn go">${t('Next song')}</button>`;
  else if (s.phase === 'gameover') h = `<button id="againBtn" class="btn go">${t('Play again')}</button><button id="lobbyBtn" class="btn">${t('Change the setup')}</button>`;
  else if (s.phase === 'intro') h = `<button id="nextBtn" class="btn go">${t('Skip intro')}</button>`;
  else if (['countdown', 'drop', 'playing', 'buzzed'].includes(s.phase)) h = `<button id="skipBtn" class="btn ghost">${t('Skip song')}</button>`;
  if (['intro', 'countdown', 'drop', 'playing', 'buzzed', 'reveal', 'standings'].includes(s.phase)) h = `<button id="pauseBtn" class="btn ${s.paused ? 'go' : 'ghost'}">${s.paused ? t('Resume') : t('Pause')}</button>` + (s.paused ? '' : h);
  if (!['lobby', 'loading', 'gameover'].includes(s.phase)) h += `<button id="endBtn" class="btn ghost">${t('End')}</button>`;
  c.innerHTML = h;
  const nb = $('#nextBtn'), sb = $('#skipBtn'), eb = $('#endBtn'), ab = $('#againBtn'), lb = $('#lobbyBtn'), pb = $('#pauseBtn');
  if (pb) pb.onclick = togglePause;
  if (ab) ab.onclick = () => conn!.send({ t: 'start' });
  if (lb) lb.onclick = () => conn!.send({ t: 'lobby' });
  if (nb) nb.onclick = () => conn!.send({ t: 'next' });
  if (sb) sb.onclick = () => conn!.send({ t: 'skip' });
  // Two presses to end, instead of a browser dialog that TV remotes and Cast can't answer.
  if (eb) eb.onclick = () => {
    if (Date.now() - endArmed < 4000) return conn!.send({ t: 'lobby' });
    endArmed = Date.now(); eb.textContent = t('Press again to end');
    setTimeout(() => { if ($('#endBtn') === eb) eb.textContent = t('End'); }, 4000);
  };
  if (keep && $('#' + keep)) $('#' + keep).focus();
  else if (nb && s.phase === 'reveal') nb.focus();
  else if (ab) ab.focus();
}


// ------------------------------------------------------------ the clocks: countdown, buzz seconds, the song's time left, the next song
let lastClock = '', lastSecs = '', x2Told: string | null = null;
// "Next song in 6": the words, then the seconds in a round sticker of their own (a unit after the number, as in "8 s",
// goes: the sticker is the seconds). Only the number changes each second; new words narrow their letters until their
// longest word fits ("Clasificación final en"), so a word is never broken.
const NUM = '\u0001';
function setNext(el: HTMLElement, text: string, n: number) {
  const [a, b = ''] = text.split(NUM), words = (a.trim() + ' ' + b.replace(/^[\s\u00a0]*s\b/, '').trim()).trim();
  if (el.dataset.words !== words) {
    el.dataset.words = words;
    el.innerHTML = `<span class="nl">${esc(words)}</span><span class="tn"></span>`;
    const nl = $('.nl', el); let k = 88;
    while (nl.scrollWidth > nl.clientWidth + 1 && k > 64) { k -= 6; nl.style.fontStretch = k + '%'; }
  }
  const tn = $('.tn', el); if (tn.textContent !== String(n)) tn.textContent = String(n);
}
// the song has been going a few seconds: any ×2 on it comes out, with the joker's stamp
function x2News(s: GameView) {
  if (s.phase !== 'playing' || !s.song || x2Told === s.song.key || !x2Out(s)) return;
  x2Told = s.song.key;
  const plays = s.jokerPlays.filter((x) => x.kind === 'double');
  if (!plays.length) return;
  A.sfx('joker');
  plays.forEach((x) => jokerBanner(s, x));
  renderChart(s);
}
// The cast bounces on the song's real beat: the low end jumping above its own recent average is a kick. A kick is one short
// animation on each character, and the browser runs those by itself: the page does nothing between kicks. (It used to be
// a CSS variable on the row, and each write made the browser go through every shape of every character, every frame.)
const lowEnd = new Uint8Array(64);
let lowAvg = 0, kickSeen = 0;
const KICK: Keyframe[] = [{ translate: '0 -.45rem', scale: '1.05 .95' }, { translate: '0 0', scale: '1 1' }];
function beat(s: GameView) {
  if (!['playing', 'reveal'].includes(s.phase) || !A.isPlaying()) return;
  A.levels(lowEnd);
  const low = (lowEnd[0] + lowEnd[1] + lowEnd[2]) / 3 / 255, now = performance.now();
  lowAvg += (low - lowAvg) * 0.05;
  if (low > lowAvg * 1.18 && low > 0.25 && now - kickSeen > 240) {
    kickSeen = now;
    if (!reducedMotion()) document.querySelectorAll('#rows .av').forEach((av) => av.animate(KICK, { duration: 450, easing: 'cubic-bezier(.16,1,.3,1)' }));
  }
}
// A screen that can't keep up says so: a spell under 40 frames a second counts against it and a good one only forgives a
// little, as the calm screens in between run fine anyway. Two against it, and the show drops to what a slow TV can do
// (.av-lite, style.css). A gap of a second isn't slowness: the page was away.
const SPELL = 2000;
let spellAt = 0, spellN = 0, lastFrame = 0, slowSpells = 0;
function pace(now: number) {
  const gap = now - lastFrame; lastFrame = now;
  if (document.documentElement.classList.contains('av-lite')) return;
  if (gap > 1000) { spellAt = now; spellN = 0; slowSpells = 0; return; }
  spellN++;
  if (now - spellAt < SPELL) return;
  slowSpells = Math.max(0, slowSpells + (spellN * 1000 / (now - spellAt) < 40 ? 1 : -.25));
  spellAt = now; spellN = 0;
  if (slowSpells >= 2) { document.documentElement.classList.add('av-lite'); track('tv_slow', { phase: (S && S.phase) || 'lobby', players: S ? S.players.length : 0 }); }
}
function frame(now: number) {
  requestAnimationFrame(frame);
  pace(now);
  const s = S; if (!s || s.paused) return; // paused: every clock on screen holds still
  if (s.phase === 'countdown') {
    const n = secsLeft(conn!, s.phaseEndsAt!), el = $('#cnum');
    if (el && n !== lastCount && n > 0) {
      (el as NumberText).textContent = n; flapIn(el); lastCount = n;
      const rows = $('#rows'); if (rows && !reducedMotion()) rows.animate([{ transform: 'none' }, { transform: 'translateY(.4rem) scale(1.01, .97)', offset: .25 }, { transform: 'none' }], { duration: 300, easing: 'ease-out' });
    }
  }
  if (s.phase === 'buzzed' && s.buzz) {
    const el = $('#buzzSecs'), v = String(secsLeft(conn!, s.buzz.deadline));
    if (el && v !== lastSecs) { lastSecs = v; el.textContent = v; }
  }
  if (s.phase === 'standings' && s.standings) {
    const el = $('#nextIn'); if (el) setNext(el, t('Round {n} in {s}s', { n: s.standings.round + 1, s: NUM }), secsLeft(conn!, s.phaseEndsAt!));
  }
  if (s.phase === 'reveal') {
    const el = $('#nextIn'), n = secsLeft(conn!, s.phaseEndsAt!);
    if (el) setNext(el, el.dataset.last ? t('Final board in {n}', { n: NUM }) : t('Next song in {n}', { n: NUM }), n);
  }
  beat(s);
  x2News(s);
  const clock = $('#clock');
  if (clock && s.song && s.phase === 'playing') {
    const b = A.buf();
    const tp = s.song.type;
    const v = tp === 'hum' || tp === 'drop' || (tp === 'lyrics' && s.lyric && s.lyric.open) ? String(secsLeft(conn!, s.phaseEndsAt!))
      : tp === 'snippet' || tp === 'medley' || tp === 'lyrics' ? '' : b && A.ready() && A.isPlaying() ? String(Math.max(0, Math.ceil((b.duration - A.pos()) / (s.song.rate || 1)))) : ''; // seconds of real time: a sped-up or slowed song runs at its rate
    if (v !== lastClock) { lastClock = v; clock.textContent = v; clock.style.visibility = v ? 'visible' : 'hidden'; }
  }
}
requestAnimationFrame(frame);

// ------------------------------------------------------------ out of sight
// The TV page hidden (another app on the TV, the TV asleep, another tab): silence at once, and a show in progress
// pauses until someone presses Resume. Back in sight the sound comes back (the show stays paused).
// Several things can say so (the page, and on a Cast receiver the TV's own input and standby signals): it's out of
// sight while any one of them says so, and one coming back doesn't undo another still away.
const away = { page: false, input: false, standby: false };
let onAway: ((gone: boolean) => void) | null = null; // the Cast receiver's 3-minute goodbye
function outOfSight(why: keyof typeof away, gone: boolean) {
  const was = Object.values(away).some(Boolean);
  away[why] = !!gone;
  const hidden = Object.values(away).some(Boolean);
  if (hidden === was) return;
  A.sleep(hidden);
  if (hidden && S && !S.paused && !['lobby', 'gameover'].includes(S.phase)) conn!.send({ t: 'pause' });
  if (onAway) onAway(hidden);
}
document.addEventListener('visibilitychange', () => outOfSight('page', document.visibilityState === 'hidden'));

// ------------------------------------------------------------ Chromecast / Google TV: this page is also the Cast receiver.
// The phone that casts says what the TV is for, and keeps asking until it answers:
//   {t:'room', pid}      the home page and the apps: the TV opens a room and answers its code, and the phone joins it
//                        (a TV already following a room answers that one, so the phone joins the game it shows); pid is
//                        that phone's player, who keeps the remote (the engine's owner)
//   {t:'follow', room}   a phone in a game, from its "Sound and screen" sheet: the TV follows that room, for another
//                        house, and answers {following} once the game is on it, or {noroom}
// A TV running something else when asked to follow starts again on the new address (the phone hears it first).
if (RECEIVER) {
  const NS = 'urn:x-cast:app.songbuzz';
  // a phone that never says (or loses its connection first) still gets a show of its own
  const lead = () => start();
  setTimeout(lead, 10000);
  const s = document.createElement('script');
  s.src = 'https://www.gstatic.com/cast/sdk/libs/caf_receiver/v3/cast_receiver_framework.js';
  s.onerror = lead;
  s.onload = () => {
    const ctx = cast.framework.CastReceiverContext.getInstance();
    ctx.addCustomMessageListener(NS, (e) => {
      const d = (e.data || {}) as { t?: string; room?: unknown; pid?: unknown }, want = d.t === 'follow' ? roomCode(d.room) : '';
      const answer = (m: Record<string, unknown>) => ctx.sendCustomMessage(NS, e.senderId, m);
      if (want.length === 4) {
        if (!started) { WATCH = want; start(); return; }
        if (WATCH !== want) { answer({ following: want, reload: true }); setTimeout(() => location.replace(`/tv?cast=1&r=${want}`), 400); return; }
        if (noRoom === want) answer({ noroom: want });
        else if (S && !gone) answer({ following: want });
        return;
      }
      lead();
      // (sent each time it asks: the room may not be open yet the first time)
      if (!WATCH && typeof d.pid === 'string' && conn) conn.send({ t: 'owner', pid: d.pid });
      if (S && S.joinUrl) answer({ room: S.room, joinUrl: S.joinUrl });
    });
    const opts = new cast.framework.CastReceiverOptions();
    opts.disableIdleTimeout = true; // a quiz has no media session to keep the receiver alive
    opts.skipPlayersLoad = true;
    opts.statusText = 'SongBuzz';
    opts.customNamespaces = { [NS]: cast.framework.system.MessageType.JSON };
    // the receiver never times out on its own (a quiz has no media session), so it closes itself when it's left
    // behind: hidden or in standby for 3 minutes (someone went to YouTube), it stops and the TV gets its sound back
    let leftAt: number | null = null;
    onAway = (gone) => { clearTimeout(leftAt!); leftAt = gone ? setTimeout(() => ctx.stop(), 3 * 60 * 1000) : null; };
    if (Object.values(away).some(Boolean)) onAway(true);
    ctx.addEventListener(cast.framework.system.EventType.VISIBILITY_CHANGED, (e) => outOfSight('input', e.isVisible === false));
    ctx.addEventListener(cast.framework.system.EventType.STANDBY_CHANGED, (e) => outOfSight('standby', e.isStandby === true));
    ctx.start(opts);
  };
  document.head.appendChild(s);
}

